import { useState, useRef, useCallback, useEffect } from "react";

const PULL_THRESHOLD = 44; // px，下拉触发阈值

/**
 * 下拉加载历史消息。
 *
 * loadMore 流程：
 *   - 取列表第一条消息的 time / idServer 作为游标，调用 getHistoryMsgActive 向前拉 50 条
 *   - 加载完成后用 scrollHeight 差值恢复视口位置，保持用户视角稳定
 */
export default function usePullToLoadMore({ store, sessionId, listRef, shouldScrollBottom }) {
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore,     setHasMore]     = useState(true);

  const pullIndicatorRef = useRef(null);

  // ref 镜像：供非 React 回调读取最新值
  const loadingMoreRef  = useRef(false);
  const hasMoreRef      = useRef(true);
  const pullStartY      = useRef(0);
  const isPulling       = useRef(false);
  const pullDistanceRef = useRef(0);
  const pullIsReadyRef  = useRef(false);
  const rafRef          = useRef(null);

  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  // ── 直接操作指示器 DOM（拖拽期间零 React 重渲染）────────────────────────
  const applyPullDOM = useCallback((distance, isReady, animated = false) => {
    const el = pullIndicatorRef.current;
    if (!el) return;
    el.style.transition = animated ? '' : 'none';
    el.style.height     = distance + 'px';
    el.classList.toggle('imc-pull-ready', isReady);
  }, []);

  // ── 加载历史消息（保持视口稳定）─────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    const list = store.msgStore.msgs.get(sessionId) || [];
    if (!list.length) { setHasMore(false); return; }

    setLoadingMore(true);
    shouldScrollBottom.current = false;

    const el         = listRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const firstMsg   = list[0];
    try {
      const res = await store.msgStore.getHistoryMsgActive({
        sessionId,
        endTime:   firstMsg.time,
        lastMsgId: firstMsg.idServer,
        limit:     100,
      });
      if (!res || res.length < 100) setHasMore(false);
      requestAnimationFrame(() => {
        if (el) { const newHeight = el.scrollHeight; el.scrollTop = newHeight - prevHeight; }
      });
    } catch (e) {
      setHasMore(false);
    } finally {
      const ind = pullIndicatorRef.current;
      if (ind) ind.style.transition = '';
      setLoadingMore(false);
    }
  }, [store, sessionId, listRef, shouldScrollBottom]);

  // ── 重置向后加载状态 ──────────────────────────────────────────────────
  const resetHasMore = useCallback(() => {
    setHasMore(true);
    hasMoreRef.current = true;
  }, []);

  // ── 绑定 touch 手势（passive:false 才能 preventDefault）───────────────
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (el.scrollTop > 16) return;
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      pullStartY.current      = e.touches[0].clientY;
      isPulling.current       = true;
      pullDistanceRef.current = 0;
      pullIsReadyRef.current  = false;
    };

    const onTouchMove = (e) => {
      if (!isPulling.current) return;
      const deltaY = e.touches[0].clientY - pullStartY.current;
      if (deltaY <= 0) { cancelAnimationFrame(rafRef.current); applyPullDOM(0, false); return; }
      e.preventDefault();
      const damped          = Math.min(deltaY * 0.45, PULL_THRESHOLD * 1.5);
      pullDistanceRef.current = damped;
      pullIsReadyRef.current  = damped >= PULL_THRESHOLD;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        applyPullDOM(pullDistanceRef.current, pullIsReadyRef.current);
      });
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      cancelAnimationFrame(rafRef.current);
      if (pullIsReadyRef.current && !loadingMoreRef.current) {
        const ind = pullIndicatorRef.current;
        if (ind) ind.style.transition = 'none';
        loadMore();
      } else {
        applyPullDOM(0, false, true);
      }
      pullDistanceRef.current = 0;
      pullIsReadyRef.current  = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
      cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line
  }, [applyPullDOM, loadMore]);

  return {
    pullIndicatorRef,
    loadingMore,
    hasMore,
    loadMore,
    resetHasMore,
  };
}
